import React, { useState, useCallback, useRef, useEffect } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus';
import Mail from 'lucide-react/dist/esm/icons/mail';
import Search from 'lucide-react/dist/esm/icons/search';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Clock from 'lucide-react/dist/esm/icons/clock';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import User from 'lucide-react/dist/esm/icons/user';
import Globe from 'lucide-react/dist/esm/icons/globe';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Link from 'lucide-react/dist/esm/icons/link';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { discoveryApi } from '../../services/api/discovery';
import { formatMessageTime } from '../../utils/markdown';
import { useToast } from '../../components/ui';
import type { Stakeholder, UserLookupResult } from './types';
import { LANGUAGE_OPTIONS, LANGUAGE_LABEL_MAP } from './constants';

interface StakeholderPanelProps {
  onClose: () => void;
}

const STAKEHOLDER_STATUS_STYLE: Record<Stakeholder['status'], { icon: typeof Clock; bg: string; text: string; label: string }> = {
  invited: { icon: Mail, bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'Invited' },
  active: { icon: CheckCircle, bg: 'bg-green-500/15', text: 'text-green-400', label: 'Active' },
  completed: { icon: CheckCircle, bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Completed' },
};

const StakeholderPanel: React.FC<StakeholderPanelProps> = ({ onClose }) => {
  const { activeRequest, addStakeholder, setError } = useDiscoveryStore();
  const { success: toastSuccess } = useToast();

  const [email, setEmail] = useState('');
  const [contextNote, setContextNote] = useState('');
  const [language, setLanguage] = useState('');
  const [lookupResults, setLookupResults] = useState<UserLookupResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserLookupResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stakeholders = activeRequest?.stakeholders || [];
  const requestId = activeRequest?.id;

  const handleEmailChange = useCallback(
    (value: string) => {
      setEmail(value);
      setSelectedUser(null);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.length < 2) {
        setLookupResults([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        setSearching(true);
        try {
          const { data } = await discoveryApi.userLookup(value);
          setLookupResults(data);
        } catch {
          setLookupResults([]);
        } finally {
          setSearching(false);
        }
      }, 300);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSelectUser = useCallback((user: UserLookupResult) => {
    setSelectedUser(user);
    setEmail(user.email);
    setLookupResults([]);
  }, []);

  const handleAdd = useCallback(async () => {
    if (!email.trim() || !requestId) return;
    setAdding(true);
    try {
      const { data } = await discoveryApi.addStakeholder(requestId, {
        email: email.trim(),
        name: selectedUser?.display_name || undefined,
        context_note: contextNote.trim(),
        language: language || undefined,
      });
      addStakeholder(data);
      setEmail('');
      setContextNote('');
      setLanguage('');
      setSelectedUser(null);
      setShowForm(false);
    } catch (err: any) {
      const is409 = err?.response?.status === 409;
      setError(is409 ? 'This person is already a stakeholder on this request.' : 'Failed to add stakeholder');
    } finally {
      setAdding(false);
    }
  }, [email, contextNote, language, selectedUser, requestId, addStakeholder, setError]);

  return (
    <div className="w-80 border-l border-subtle bg-surface-page flex flex-col animate-fade-in-down shrink-0">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-subtle">
        <h3 className="text-sm font-semibold text-content">Stakeholders</h3>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowForm(!showForm)}
            className={`flex items-center justify-center w-7 h-7 rounded-lg transition-all ${
              showForm ? 'bg-brand/15 text-primary' : 'text-content-muted hover:text-content hover:bg-surface-secondary'
            }`}
            title="Add stakeholder"
          >
            <UserPlus size={15} />
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-lg text-content-muted hover:text-content hover:bg-surface-secondary transition-all"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="px-4 py-3 border-b border-subtle space-y-3 bg-surface-primary">
          {/* Email input with typeahead */}
          <div className="relative">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-primary border border-subtle focus-within:border-brand/40 transition-all">
              <Search size={14} className="text-content-muted shrink-0" />
              <input
                type="text"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="Search by email or name..."
                className="flex-1 bg-transparent text-sm text-content placeholder:text-content-faint focus:outline-none"
              />
              {searching && (
                <span className="w-3 h-3 border-2 border-brand/30 border-t-primary rounded-full animate-spin shrink-0" />
              )}
            </div>

            {/* Lookup dropdown */}
            {lookupResults.length > 0 && !selectedUser && (
              <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-surface-page border border-subtle rounded-lg shadow-xl max-h-48 overflow-y-auto">
                {lookupResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-surface-primary text-left transition-all"
                  >
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-surface-secondary shrink-0">
                      <User size={14} className="text-content-muted" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-content font-medium truncate">
                        {user.display_name || user.email}
                      </p>
                      <p className="text-[10px] text-content-muted truncate">{user.email}</p>
                    </div>
                    {user.is_app_user && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-brand/15 text-primary shrink-0">
                        App User
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Resolved user info */}
          {selectedUser && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand/5 border border-brand/15">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-brand/15 shrink-0">
                <User size={12} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-content font-medium truncate">{selectedUser.display_name || selectedUser.email}</p>
                {selectedUser.org_name && <p className="text-[10px] text-content-muted">{selectedUser.org_name}</p>}
              </div>
              {selectedUser.is_app_user && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-brand/15 text-primary shrink-0">
                  App User
                </span>
              )}
              <button
                onClick={() => { setSelectedUser(null); setEmail(''); }}
                className="text-content-muted hover:text-content-secondary"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <textarea
            value={contextNote}
            onChange={(e) => setContextNote(e.target.value)}
            placeholder="Relevant for... (e.g. knows the billing system)"
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-surface-primary border border-subtle text-sm text-content placeholder:text-content-faint resize-none focus:outline-none focus:border-brand/40 transition-all"
          />

          <div>
            <label className="block text-[10px] text-content-muted mb-1">
              <Globe size={10} className="inline mr-1 -mt-px" />
              Conversation language
            </label>
            <div className="flex items-center gap-1.5">
              {LANGUAGE_OPTIONS.map((opt) => {
                const active = language === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setLanguage(opt.value)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                      active
                        ? 'bg-brand/15 text-primary border border-brand/30'
                        : 'bg-surface-primary border border-subtle text-content-muted hover:text-content hover:border-default'
                    }`}
                    title={opt.label}
                  >
                    {opt.flag ? (
                      <span className={`fi fi-${opt.flag}`} />
                    ) : (
                      <Globe size={12} />
                    )}
                    {opt.label.split(' ')[0]}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={handleAdd}
            disabled={!email.trim() || adding}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-lg bg-brand text-content text-xs font-medium hover:bg-brand/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {adding ? (
              <span className="w-3 h-3 border-2 border-content/30 border-t-content rounded-full animate-spin" />
            ) : (
              <UserPlus size={14} />
            )}
            Add Stakeholder
          </button>
        </div>
      )}

      {/* Stakeholder list */}
      <div className="flex-1 overflow-y-auto">
        {stakeholders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-content-muted text-xs px-4 text-center">
            <User size={24} className="mb-2 opacity-40" />
            <p>No stakeholders yet.</p>
            <p className="mt-1 text-content-faint">Add people who can provide input on this request.</p>
          </div>
        ) : (
          <div className="py-2">
            {stakeholders.map((sh) => {
              const statusConf = STAKEHOLDER_STATUS_STYLE[sh.status] || STAKEHOLDER_STATUS_STYLE.invited;
              const StatusIcon = statusConf.icon;
              return (
                <div key={sh.id} className="px-4 py-3 hover:bg-surface-primary transition-all">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-surface-secondary shrink-0">
                      <User size={14} className="text-content-muted" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-content font-medium truncate">
                          {sh.name || sh.email}
                        </span>
                        {sh.is_external ? (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/15 text-amber-400 shrink-0">
                            External
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-500/15 text-blue-400 shrink-0">
                            Internal
                          </span>
                        )}
                        {sh.language && LANGUAGE_LABEL_MAP[sh.language] && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-violet-500/15 text-violet-400 shrink-0">
                            <span className={`fi fi-${LANGUAGE_LABEL_MAP[sh.language].flag}`} />
                            {LANGUAGE_LABEL_MAP[sh.language].label}
                          </span>
                        )}
                      </div>
                      {sh.name && <p className="text-[10px] text-content-muted truncate mt-0.5">{sh.email}</p>}
                      {sh.context_note && (
                        <p className="text-[10px] text-content-muted mt-1 leading-relaxed line-clamp-2">{sh.context_note}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className={`flex items-center gap-1 text-[10px] ${statusConf.text}`}>
                          <StatusIcon size={10} /> {statusConf.label}
                        </span>
                        {sh.last_active_at && (
                          <span className="flex items-center gap-1 text-[10px] text-content-faint">
                            <Clock size={10} /> {formatMessageTime(sh.last_active_at)}
                          </span>
                        )}
                      </div>
                      {sh.portal_url && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(sh.portal_url!);
                            toastSuccess('Link copied');
                          }}
                          className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md bg-surface-secondary text-[10px] text-content-muted hover:text-content hover:bg-surface-elevated transition-all group/link"
                          title={sh.portal_url}
                        >
                          <Link size={10} className="shrink-0" />
                          <span className="truncate max-w-[140px]">{sh.portal_url}</span>
                          <Copy size={10} className="shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(StakeholderPanel);
